<template>
    <b-row class="p-3">
    <b-col sm="12">
        <InputField name="name" :label="$t('name')" v-model="fieldName" @error="fieldError"
            :rules="{required:true, length: {min: 2}}"
            :validated="true"
        />

        <b-form-group
            :label="this.$t('label')"
            label-for="label"
            :label-cols="12"
            :horizontal="false">
            <Texti18n taggable v-model="field.label" />
        </b-form-group>

        <InputField name="key" :label="this.$t('key')" v-model="fieldKey" @error="fieldError"
            :rules="{required:true, length: {min: 2}}"
            :description="$t('fieldsKeyUpdateDescription')"
        />

        <b-form-group
            v-if="!config.disableSchemas"
            :label="this.$t('schema')"
            :description="this.$t('schemaDescription')"
            label-for="schema"
            :label-cols="12"
            :horizontal="false">
            <v-select 
                v-model="field.schema" 
                :options="availableSchemas" 
                taggable 
                :create-option="schema => schema"
                :placeholder="$t('selectOrCreateSchema')"
            />
        </b-form-group>

        <template v-if="!config.custom">
        <b-form-group
            :label="this.$t('description')"
            label-for="description"
            :label-cols="12"
            :horizontal="false">
            <Texti18n taggable v-model="field.description" />
        </b-form-group>

        <b-form-group v-if="!this.config.hideProtected"
            :label="this.$t('protected')"
            :description="this.$t('protectedField')"
            label-for="protected"
            :horizontal="false">
            <c-switch class="mx-1" color="success" variant="pill" v-model="field.protected"/>
        </b-form-group>

        <b-form-group v-if="!this.config.hideVisible"
            :label="this.$t('visible')"
            :description="this.$t('fieldNotVisible')"
            label-for="visible"
            :horizontal="false">
            <c-switch class="mx-1" color="success" variant="pill" v-model="field.visible"/>
        </b-form-group>
        </template>

    </b-col>
    <template v-if="!config.custom" >
        <b-col sm="12">
            <b-row sm="6">
                <b-col sm="12">
                    <SelectField name="module" :label="this.$t('module')" v-model="field.module" @error="fieldError"
                        :items="modules"
                        :reduce="(o) => o"
                        :rules="{required: true, protected: true}"
                    />

                    <SelectField name="type" :label="this.$t('type')" v-model="field.type" @error="fieldError"
                        :items="types"
                        :reduce="(o) => o"
                        :rules="{required: true, protected: true}"
                    />

                    <b-form-group
                    :label="this.$t('constraints')"
                    label-for="constraints"
                    :horizontal="false">
                    <ConstraintsEditor v-model="field.constraints" :field="field" :component="component"/>
                </b-form-group>
                <b-form-group
                    v-if="canDisplayRelationships(field)"
                    :label="this.$t('relationships')"
                    label-for="relationships"
                    :horizontal="false">
                    <RelationshipsEditor v-model="field.relation" :fields="fields" :field="field"/>
                </b-form-group>
                <b-form-group
                    v-if="canDisplayTemplate(field)"
                    :label="this.$t('providedTemplate')"
                    label-for="template"
                    :horizontal="false">
                    <c-switch v-model="field.provided_template" />
                </b-form-group>
                <b-form-group
                    v-if="canDisplayTemplate(field) && field.provided_template"
                    :label="this.$t('template')"
                    label-for="template"
                    :horizontal="false">

                        <Entrypoints v-model="entrypoint" :settings="component.settings" />

                </b-form-group>
                </b-col>
            </b-row>
        </b-col>
    </template>
    </b-row>
</template>

<script>
import fieldDefs from "@/components/identityFields/defsv2"
import IdentityFields from '@/components/identityFields/IdentityFields'
import Mutators from "./Mutators"
import Relationships from "./Relationships"
import CustomTemplate from "./Template"
import Constraints from "./Constraints"
import Context from "./Context"
import { Switch as cSwitch } from '@coreui/vue'
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import _ from "lodash"
import Helptip from '../../elements/Helptip.vue'
import VSelect from 'vue-select'
import draggable from 'vuedraggable'
import Entrypoints from './Entrypoints'

// css
import Texti18n from "../../form/Texti18n.vue"

export default {
    components: {
        MutatorsEditor: Mutators,
        RelationshipsEditor: Relationships,
        ConstraintsEditor: Constraints,
        ContextEditor: Context,
        cSwitch,
        InputField: Input,
        SelectField: Select,
        Helptip,
        VSelect,
        Texti18n,
        CustomTemplate,
        IdentityFields,
        draggable,
        Entrypoints,
    },

    props: {
        value: {
            type: Object
        },

        fields: {
            type: Array
        },

        config: {
            type: Object,
            default: function() {
            return {
                fieldDefs: fieldDefs,
                hideGroup: false,
                hideProtected: false,
                hideVisible: false,
                fieldSize: 4,
                custom: false,
                dependencyProtected: false,
                disableSchemas: false,
            }
            }
        },

        component: {
            type: Object
        }
    },

    data: function() {
        return {
            errors: [],
        }
    },

    methods: {
        canDisplayRelationships(field) {
            let mod = field.module
            let type = field.type

            switch(mod) {
                case 'select': return type == 'relation';
                case 'table': return type == 'default';
                default: return false
            }
        },

        canDisplayTemplate(field) {
            let mod = field.module

            switch(mod) {
                default: return true
            }
        },

        fieldError(error) {
            let found = this.errors.find((i) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },
    },

    computed: {
        availableSchemas() {
            if (!this.fields || !Array.isArray(this.fields)) {
                return []
            }
            
            // Get unique schemas from existing fields
            const schemas = new Set()
            this.fields.forEach(field => {
                if (field.schema) {
                    schemas.add(field.schema)
                }
            })
            
            return Array.from(schemas).sort()
        },

        fieldDefs() {
            if (this.config !== null) {
                if (this.config.fieldDefs) {
                    return this.config.fieldDefs
                }
            }

            return fieldDefs
        },
        
        modules() {
            return this.fieldDefs.modules.map(m => {
                return m.key
            })
        },

        types() {
            const module = this.fieldDefs.modules.find(m => {
                return m.key == this.field.module
            })

            const { fields = [] } = module || {}

            return fields.map(f => {
                return f.key
            })
        },

        fieldName: {
            get() {
                return this.field.name
            },

            set(val) {
                this.field.name = _.startCase(_.toLower(val))
            }
        },

        fieldKey: {
            get() {
                return this.field.key
            },

            set(val) {
                this.field.key = val.replaceAll(" ", "_", _.toLower(val))
            } 
        },

        availablePermissions() {
            let perms = ["public-app","platform-app","public-api"]

            let publicApps = this.$store.getters.getPublicApps || []
            publicApps.forEach(p => {
                perms.push(`public-app::${p.name}`)
            })

            let platformApps = this.$store.getters.getApps || []
            platformApps.forEach(p => {
                perms.push(`platform-app::${p.name}`)
            })

            return perms
        },

        field: {
            get() {
                return this.value
            },

            set(val) {
                this.$emit('input', val)
            }
        },

        fieldGroups() {
            let groups = {
                '': false,
            }

            this.fields.forEach(f => {
                if (f.group !== undefined) {
                groups[f.group] = true
                }
            })

            return Object.keys(groups).filter(g => {
                return groups[g]
            });
        },

        entrypoint: {
            get() {
                return {
                    component: this.field.provided_template_component,
                    template: this.field.component_template
                }
            },

            set(v) {
                this.field = Object.assign({}, this.field, {
                    provided_template_component: v.component,
                    component_template: v.template
                })
            }
            
        }
    },

    mounted() {
        console.log("Mounted field editor", this.fields)
    }
}
</script>